import type { ColorObject } from '@/types/app/color.type'
import { AssetFamilyTypeId } from '@/types/hierarchy/asset-family-type-id.enum'
import { TAG_COLORS } from '@/utils/constants/colors'
import { objectKeysToLowerCase } from '@/utils/object/object-keys-to-lower-case'

export function getBatteryBankPhaseColor(phase: string): undefined | 'error' | 'help' | 'primary' | 'success' | 'warning' {
  switch (phase.toLocaleLowerCase()) {
    case 'boost':
    case 'float':
    case 'recharge':
      return 'success'

    case 'discharge':
      return 'warning'

    case 'open circuit':
      return 'primary'

    default:
      return undefined
  }
}

export function getBatteryBankPhaseColorObject(phase: string): ColorObject {
  let _object = TAG_COLORS.GREY

  switch (phase.toLocaleLowerCase()) {
    case 'boost':
    case 'float':
    case 'recharge':
      _object = TAG_COLORS.GREEN
      break

    case 'discharge':
      _object = TAG_COLORS.YELLOW
      break

    case 'open circuit':
      _object = TAG_COLORS.BLUE
      break
  }
  return objectKeysToLowerCase(_object) as unknown as ColorObject
}

export function isBatteryBank(assetFamilyTypeId: AssetFamilyTypeId): boolean {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
    case AssetFamilyTypeId.BatteryBankLFP:
    case AssetFamilyTypeId.BatteryBankNMC:
    case AssetFamilyTypeId.BatteryBankVRLA:
      return true
    default:
      return false
  }
}

export function isBatteryBankLeadAcid(assetFamilyTypeId: AssetFamilyTypeId): boolean {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
    case AssetFamilyTypeId.BatteryBankVRLA:
      return true
    default:
      return false
  }
}

export function isBatteryBankLithium(assetFamilyTypeId: AssetFamilyTypeId): boolean {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankLFP:
    case AssetFamilyTypeId.BatteryBankNMC:
      return true
    default:
      return false
  }
}

const LEAD_ACID_VOLTAGE_STEP = 2
const LEAD_ACID_MAX_VOLTAGE = 240
const LFP_VOLTAGES = [12, 12.8, 24, 25.6, 48, 51.2]
const MAX_TOTAL_SYSTEM_VOLTAGE = 1500
const MAX_TOTAL_SYSTEM_VOLTAGE_OPTIONS = 120

function roundVoltage(voltage: number): number {
  return Number(voltage.toFixed(1))
}

// NMC has no agreed set of nominal voltages, so it stays free entry (undefined).
export function getIndividualBatteryVoltageOptions(assetFamilyTypeId: AssetFamilyTypeId | undefined): number[] | undefined {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
    case AssetFamilyTypeId.BatteryBankVRLA:
      return Array.from(
        { length: LEAD_ACID_MAX_VOLTAGE / LEAD_ACID_VOLTAGE_STEP },
        (_, index) => (index + 1) * LEAD_ACID_VOLTAGE_STEP,
      )

    case AssetFamilyTypeId.BatteryBankLFP:
      return [...LFP_VOLTAGES]

    default:
      return undefined
  }
}

export function canOverrideIndividualBatteryVoltage(assetFamilyTypeId: AssetFamilyTypeId | undefined): boolean {
  return assetFamilyTypeId === AssetFamilyTypeId.BatteryBankLFP
}

export function getTotalSystemVoltageOptions(individualBatteryVoltage: number | undefined): number[] | undefined {
  if (!individualBatteryVoltage || individualBatteryVoltage <= 0) {
    return undefined
  }

  const optionCount = Math.min(
    Math.floor(MAX_TOTAL_SYSTEM_VOLTAGE / individualBatteryVoltage),
    MAX_TOTAL_SYSTEM_VOLTAGE_OPTIONS,
  )

  if (optionCount === 0) {
    return undefined
  }

  return Array.from({ length: optionCount }, (_, index) => roundVoltage((index + 1) * individualBatteryVoltage))
}

const MAX_BATTERY_STRINGS = 100

export function getBatteriesPerString(
  individualBatteryVoltage: number | undefined,
  totalSystemVoltage: number | undefined,
): number | undefined {
  if (!individualBatteryVoltage || !totalSystemVoltage || individualBatteryVoltage <= 0) {
    return undefined
  }

  const batteriesPerString = Math.round(totalSystemVoltage / individualBatteryVoltage)

  return batteriesPerString >= 1 ? batteriesPerString : undefined
}

// The number of parallel strings is unknown, so any multiple of the per-string count is valid.
export function getBatteryCountOptions(
  individualBatteryVoltage: number | undefined,
  totalSystemVoltage: number | undefined,
): number[] | undefined {
  const batteriesPerString = getBatteriesPerString(individualBatteryVoltage, totalSystemVoltage)

  if (!batteriesPerString) {
    return undefined
  }

  return Array.from({ length: MAX_BATTERY_STRINGS }, (_, index) => (index + 1) * batteriesPerString)
}
